import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { pushNotificationService, NotificationPermission, PushNotificationData } from '../services/pushNotificationService';
import { useNotifications } from './NotificationContext';

interface PushNotificationContextType {
  isSupported: boolean;
  permission: NotificationPermission;
  isEnabled: boolean;
  isLoading: boolean;
  requestPermission: () => Promise<void>;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
  updateSettings: (settings: {
    orderUpdates?: boolean;
    shippingUpdates?: boolean;
    couponExpiry?: boolean;
    promotionalOffers?: boolean;
  }) => Promise<void>;
  settings: {
    orderUpdates: boolean;
    shippingUpdates: boolean;
    couponExpiry: boolean;
    promotionalOffers: boolean;
  };
}

const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined);

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider');
  }
  return context;
};

interface PushNotificationProviderProps {
  children: ReactNode;
}

export const PushNotificationProvider: React.FC<PushNotificationProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { addNotification } = useNotifications();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>({
    granted: false,
    denied: false,
    default: true
  });
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState({
    orderUpdates: true,
    shippingUpdates: true,
    couponExpiry: true,
    promotionalOffers: false
  });

  // Initialize push notifications (only once)
  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null | undefined = null;

    const initializePushNotifications = async () => {
      try {
        const supported = await pushNotificationService.initialize();
        if (!isMounted) return;
        
        setIsSupported(supported);

        if (supported) {
          // Check current permission status
          const currentPermission = await pushNotificationService.requestPermission();
          if (!isMounted) return;
          
          setPermission(currentPermission);
          setIsEnabled(currentPermission.granted);

          // Set up message listener
          unsubscribe = pushNotificationService.onMessageReceived((payload) => {
            console.log('Message received:', payload);
            
            // Show local notification
            if (payload.notification) {
              pushNotificationService.showLocalNotification({
                title: payload.notification.title || 'StringPak Update',
                body: payload.notification.body || 'You have a new update',
                data: payload.data
              });
            }

            // Show in-app notification
            addNotification({
              type: 'info',
              title: payload.notification?.title || 'New Update',
              message: payload.notification?.body || 'You have a new notification',
              duration: 5000
            });
          });
        }
      } catch (error) {
        console.error('Error initializing push notifications:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializePushNotifications();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty dependency array - only initialize once

  // Load user settings when user changes
  useEffect(() => {
    const loadUserSettings = async () => {
      if (currentUser) {
        try {
          const userSettings = await pushNotificationService.getNotificationSettings(currentUser.uid);
          setSettings(userSettings);
        } catch (error) {
          console.error('Error loading notification settings:', error);
        }
      }
    };

    loadUserSettings();
  }, [currentUser]);

  const requestPermission = async () => {
    if (!isSupported) {
      addNotification({
        type: 'error',
        title: 'Not Supported',
        message: 'Push notifications are not supported in this browser'
      });
      return;
    }

    try {
      setIsLoading(true);
      const newPermission = await pushNotificationService.requestPermission();
      setPermission(newPermission);
      setIsEnabled(newPermission.granted);

      if (newPermission.granted) {
        // Get FCM token and save it to user
        const token = await pushNotificationService.getToken();
        if (token && currentUser) {
          await pushNotificationService.saveTokenToUser(currentUser.uid, token);
          console.log('FCM token saved to user:', token);
        }
        
        addNotification({
          type: 'success',
          title: 'Notifications Enabled',
          message: 'You will now receive order updates and important notifications'
        });
      } else if (newPermission.denied) {
        addNotification({
          type: 'warning',
          title: 'Notifications Blocked',
          message: 'Please enable notifications in your browser settings to receive updates'
        });
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      addNotification({
        type: 'error',
        title: 'Permission Error',
        message: 'Failed to request notification permission'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const enableNotifications = async () => {
    if (!currentUser) {
      addNotification({
        type: 'error',
        title: 'Authentication Required',
        message: 'Please log in to enable notifications'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Request permission first
      await requestPermission();
      
      if (permission.granted) {
        // Get FCM token
        const token = await pushNotificationService.getToken();
        
        if (token) {
          // Save token to user
          await pushNotificationService.saveTokenToUser(currentUser.uid, token);
          setIsEnabled(true);
          
          addNotification({
            type: 'success',
            title: 'Notifications Enabled',
            message: 'You will now receive order updates and important notifications'
          });
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      addNotification({
        type: 'error',
        title: 'Enable Error',
        message: 'Failed to enable notifications'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disableNotifications = async () => {
    if (!currentUser) return;

    try {
      setIsLoading(true);
      
      // Delete FCM token
      await pushNotificationService.deleteToken();
      
      // Remove token from user
      await pushNotificationService.removeTokenFromUser(currentUser.uid);
      
      setIsEnabled(false);
      setPermission({ granted: false, denied: false, default: true });
      
      addNotification({
        type: 'info',
        title: 'Notifications Disabled',
        message: 'You will no longer receive push notifications'
      });
    } catch (error) {
      console.error('Error disabling notifications:', error);
      addNotification({
        type: 'error',
        title: 'Disable Error',
        message: 'Failed to disable notifications'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (newSettings: {
    orderUpdates?: boolean;
    shippingUpdates?: boolean;
    couponExpiry?: boolean;
    promotionalOffers?: boolean;
  }) => {
    if (!currentUser) return;

    try {
      const updatedSettings = { ...settings, ...newSettings };
      await pushNotificationService.updateNotificationSettings(currentUser.uid, updatedSettings);
      setSettings(updatedSettings);
      
      addNotification({
        type: 'success',
        title: 'Settings Updated',
        message: 'Your notification preferences have been saved'
      });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      addNotification({
        type: 'error',
        title: 'Update Error',
        message: 'Failed to update notification settings'
      });
    }
  };

  const value: PushNotificationContextType = {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    requestPermission,
    enableNotifications,
    disableNotifications,
    updateSettings,
    settings
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};
